/**
 * Lightweight i18n helper for the Public Viewer.
 *
 * Each object can carry a `translations` field:
 *   { en: {name: "..."}, ar: {name: "...", fields: {Price: "..."}} }
 *
 * tr(obj, key, lang, defaultLang) returns the translated value if present.
 */
export function tr(obj, key, lang, defaultLang = "en") {
  if (!obj) return undefined;
  const t = obj.translations || {};
  if (lang && t[lang] && t[lang][key] != null && t[lang][key] !== "") return t[lang][key];
  if (defaultLang && t[defaultLang] && t[defaultLang][key] != null && t[defaultLang][key] !== "") return t[defaultLang][key];
  return obj[key];
}

export function trField(obj, fieldKey, lang, defaultLang = "en") {
  const t = obj?.translations || {};
  const langFields = (t[lang] && t[lang].fields) || {};
  if (langFields[fieldKey] != null && langFields[fieldKey] !== "") return langFields[fieldKey];
  const defFields = (t[defaultLang] && t[defaultLang].fields) || {};
  if (defFields[fieldKey] != null && defFields[fieldKey] !== "") return defFields[fieldKey];
  return obj?.fields?.[fieldKey];
}

const VIEWER_LABELS = {
  en: {
    units: "Units", contact: "Contact", overview: "Overview", back_to_tour: "Back to tour",
    all_units: "All units", enquire: "Enquire", whatsapp: "WhatsApp", call: "Call",
    floor_plan: "Floor plan", gallery: "Gallery", video: "Video",
    rotate_message: "Please rotate your device for the best experience.",
    available: "Available", reserved: "Reserved", sold: "Sold", hidden: "Hidden",
    coming_soon: "Coming soon", featured: "Featured",
    lead_name: "Your name", lead_phone: "Phone", lead_email: "Email (optional)",
    lead_message: "Message (optional)", lead_send: "Send enquiry",
    lead_thanks: "Thank you. Our team will get in touch shortly.",
    lead_continue: "Continue exploring",
    loading: "Loading experience",
    unavailable_title: "Unavailable",
    units_count: (n) => `${n} units`,
    all_statuses: "All",
    etaps: "Phases", blocks: "Blocks",
    shortlist: "Shortlist", compare: "Compare",
    add_to_shortlist: "Add to shortlist", in_shortlist: "Shortlisted", remove: "Remove",
    open: "Open", share: "Share", copy_link: "Copy link", link_copied: "Link copied",
    download_pdf: "Download PDF", pdf_failed: "Couldn’t generate PDF",
    download_qr: "Download QR",
    added_to_shortlist: "Added to shortlist", removed_from_shortlist: "Removed",
    shortlist_empty: "Your shortlist is empty",
    shortlist_empty_hint: "Open a unit and tap Add to Shortlist while presenting.",
    clear: "Clear", clear_confirm: "Clear the entire shortlist?",
    compare_min: "Add at least two units to compare.",
    unit_unavailable: "unit(s) no longer available",
    payment_plan: "Payment plan", list_price: "List price", discount: "Discount",
    vat: "VAT", total: "Total", down_payment: "Down payment",
    remaining: "Remaining", monthly: "Monthly",
    pp_disclaimer: "Indicative — final terms set in writing",
    // Phase C
    presentation_mode: "Presentation", start_presentation: "Start presentation",
    next: "Next", previous: "Previous", exit: "Exit", step_of: (a, b) => `Step ${a} of ${b}`,
    view_from_unit: "View from unit", showroom_mode: "Showroom mode",
    showroom_caching: "Caching for offline…", showroom_ready: "Showroom ready (offline)",
    showroom_failed: "Caching failed",
  },
  ar: {
    units: "الوحدات", contact: "تواصل", overview: "العرض الرئيسي", back_to_tour: "العودة إلى الجولة",
    all_units: "جميع الوحدات", enquire: "استفسار", whatsapp: "واتساب", call: "اتصال",
    floor_plan: "المخطط", gallery: "المعرض", video: "فيديو",
    rotate_message: "يرجى تدوير جهازك للحصول على أفضل تجربة.",
    available: "متاحة", reserved: "محجوزة", sold: "مباعة", hidden: "مخفية",
    coming_soon: "قريباً", featured: "مميزة",
    lead_name: "الاسم", lead_phone: "الهاتف", lead_email: "البريد الإلكتروني (اختياري)",
    lead_message: "الرسالة (اختياري)", lead_send: "إرسال الاستفسار",
    lead_thanks: "شكراً لك. سيتواصل معك فريقنا قريباً.",
    lead_continue: "متابعة الاستكشاف",
    loading: "جارٍ تحميل التجربة",
    unavailable_title: "غير متاح",
    units_count: (n) => `${n} وحدة`,
    all_statuses: "الكل",
    etaps: "المراحل", blocks: "المباني",
    shortlist: "المفضلة", compare: "مقارنة",
    add_to_shortlist: "أضف إلى المفضلة", in_shortlist: "في المفضلة", remove: "إزالة",
    open: "فتح", share: "مشاركة", copy_link: "نسخ الرابط", link_copied: "تم نسخ الرابط",
    download_pdf: "تنزيل PDF", pdf_failed: "تعذّر إنشاء ملف PDF",
    added_to_shortlist: "تمت الإضافة إلى المفضلة", removed_from_shortlist: "تمت الإزالة",
    shortlist_empty: "المفضلة فارغة",
    shortlist_empty_hint: "افتح وحدة واضغط أضف إلى المفضلة أثناء العرض.",
    clear: "مسح", clear_confirm: "هل تريد مسح المفضلة بالكامل؟",
    compare_min: "أضف وحدتين على الأقل للمقارنة.",
    unit_unavailable: "وحدة/وحدات لم تعد متوفرة",
    payment_plan: "خطة السداد", list_price: "السعر المعلن", discount: "الخصم",
    vat: "ضريبة", total: "الإجمالي", down_payment: "الدفعة المقدمة",
    remaining: "المتبقي", monthly: "القسط الشهري", download_qr: "تنزيل رمز QR",
    pp_disclaimer: "تقديرية — تُحدّد الشروط النهائية كتابياً",
    presentation_mode: "العرض", start_presentation: "بدء العرض",
    next: "التالي", previous: "السابق", exit: "خروج", step_of: (a, b) => `الخطوة ${a} من ${b}`,
    view_from_unit: "الإطلالة من الوحدة", showroom_mode: "وضع المعرض",
    showroom_caching: "جارٍ التخزين للعمل دون اتصال…", showroom_ready: "المعرض جاهز (دون اتصال)",
    showroom_failed: "فشل التخزين",
  },
  tr: {
    units: "Daireler", contact: "İletişim", overview: "Genel", back_to_tour: "Tura geri dön",
    all_units: "Tüm daireler", enquire: "Başvur", whatsapp: "WhatsApp", call: "Ara",
    floor_plan: "Plan", gallery: "Galeri", video: "Video",
    rotate_message: "En iyi deneyim için cihazınızı yatay konuma getirin.",
    available: "Müsait", reserved: "Rezerve", sold: "Satıldı", hidden: "Gizli",
    coming_soon: "Yakında", featured: "Öne çıkan",
    lead_name: "Adınız", lead_phone: "Telefon", lead_email: "E-posta (opsiyonel)",
    lead_message: "Mesaj (opsiyonel)", lead_send: "Gönder",
    lead_thanks: "Teşekkürler. Ekibimiz kısa süre içinde sizinle iletişime geçecek.",
    lead_continue: "Keşfetmeye devam et",
    loading: "Deneyim yükleniyor",
    unavailable_title: "Mevcut değil",
    units_count: (n) => `${n} daire`,
    all_statuses: "Hepsi",
    etaps: "Etaplar", blocks: "Bloklar",
    shortlist: "Liste", compare: "Karşılaştır",
    add_to_shortlist: "Listeye ekle", in_shortlist: "Listede", remove: "Kaldır",
    open: "Aç", share: "Paylaş", copy_link: "Bağlantıyı kopyala", link_copied: "Bağlantı kopyalandı",
    download_pdf: "PDF indir", pdf_failed: "PDF oluşturulamadı",
    added_to_shortlist: "Listeye eklendi", removed_from_shortlist: "Kaldırıldı",
    shortlist_empty: "Listeniz boş",
    shortlist_empty_hint: "Sunum sırasında bir daire açıp Listeye Ekle’ye dokunun.",
    clear: "Temizle", clear_confirm: "Tüm liste temizlensin mi?",
    compare_min: "Karşılaştırmak için en az iki daire ekleyin.",
    unit_unavailable: "daire artık müsait değil",
    payment_plan: "Ödeme planı", list_price: "Liste fiyatı", discount: "İndirim",
    vat: "KDV", total: "Toplam", down_payment: "Peşinat",
    remaining: "Kalan", monthly: "Aylık taksit", download_qr: "QR indir",
    pp_disclaimer: "Tahmini — nihai şartlar yazılı sözleşme ile belirlenir",
    presentation_mode: "Sunum", start_presentation: "Sunumu başlat",
    next: "İleri", previous: "Geri", exit: "Çıkış", step_of: (a, b) => `Adım ${a} / ${b}`,
    view_from_unit: "Daireden manzara", showroom_mode: "Showroom modu",
    showroom_caching: "Çevrimdışı için önbelleğe alınıyor…", showroom_ready: "Showroom hazır (çevrimdışı)",
    showroom_failed: "Önbellekleme başarısız",
  },
  ru: {
    units: "Юниты", contact: "Контакт", overview: "Обзор", back_to_tour: "К туру",
    all_units: "Все юниты", enquire: "Запросить", whatsapp: "WhatsApp", call: "Позвонить",
    floor_plan: "Планировка", gallery: "Галерея", video: "Видео",
    rotate_message: "Поверните устройство для лучшего опыта.",
    available: "Свободно", reserved: "Резерв", sold: "Продано", hidden: "Скрыто",
    coming_soon: "Скоро", featured: "Избранное",
    lead_name: "Ваше имя", lead_phone: "Телефон", lead_email: "E-mail (опц.)",
    lead_message: "Сообщение (опц.)", lead_send: "Отправить",
    lead_thanks: "Спасибо. Мы скоро свяжемся с вами.",
    lead_continue: "Продолжить просмотр",
    loading: "Загрузка",
    unavailable_title: "Недоступно",
    units_count: (n) => `${n} юнитов`,
    all_statuses: "Все",
    etaps: "Этапы", blocks: "Блоки",
    shortlist: "Подборка", compare: "Сравнить",
    add_to_shortlist: "Добавить в подборку", in_shortlist: "В подборке", remove: "Убрать",
    open: "Открыть", share: "Поделиться", copy_link: "Скопировать ссылку", link_copied: "Ссылка скопирована",
    download_pdf: "Скачать PDF", pdf_failed: "Не удалось создать PDF",
    added_to_shortlist: "Добавлено в подборку", removed_from_shortlist: "Удалено",
    shortlist_empty: "Подборка пуста",
    shortlist_empty_hint: "Откройте юнит и нажмите «Добавить» во время презентации.",
    clear: "Очистить", clear_confirm: "Очистить всю подборку?",
    compare_min: "Добавьте минимум два юнита для сравнения.",
    unit_unavailable: "юнит(ов) больше недоступно",
    payment_plan: "Рассрочка", list_price: "Цена", discount: "Скидка",
    vat: "НДС", total: "Итого", down_payment: "Первый взнос",
    remaining: "Остаток", monthly: "Ежемесячно", download_qr: "Скачать QR",
    pp_disclaimer: "Ориентировочно — финальные условия согласуются письменно",
    presentation_mode: "Презентация", start_presentation: "Начать презентацию",
    next: "Далее", previous: "Назад", exit: "Выход", step_of: (a, b) => `Шаг ${a} из ${b}`,
    view_from_unit: "Вид из юнита", showroom_mode: "Шоурум",
    showroom_caching: "Кэширование для офлайн…", showroom_ready: "Шоурум готов (офлайн)",
    showroom_failed: "Ошибка кэширования",
  },
};

// Public controls share a complete dictionary in each supported language.
const EXTRA_LABELS = {
  "en": {
    "close": "Close",
    "find": "Find",
    "find_unit": "Find unit",
    "floors": "Floors",
    "location": "Location",
    "sort": "Sort",
    "recommended": "Recommended",
    "sort_price_asc": "Price · low → high",
    "sort_price_desc": "Price · high → low",
    "sort_area_asc": "Area · low → high",
    "sort_area_desc": "Area · high → low",
    "sort_floor_asc": "Floor · low → high",
    "sort_floor_desc": "Floor · high → low",
    "sort_code": "Unit code (natural)",
    "sort_status": "Status",
    "status": "Status",
    "floor": "Floor",
    "unit": "Unit",
    "code": "Unit code",
    "type": "Type",
    "view": "View",
    "net_area": "Net area",
    "gross_area": "Gross area",
    "price": "Price",
    "currency": "Currency",
    "area": "Area",
    "area_sqm": "Area",
    "beds": "Bedrooms",
    "bedrooms": "Bedrooms",
    "bathrooms": "Bathrooms",
    "phase": "Phase",
    "block": "Block",
    "phase_block": "Phase › Block",
    "any": "Any",
    "yes": "Yes",
    "no": "No",
    "search": "Search…",
    "min": "Min",
    "max": "Max",
    "from": "From",
    "to": "To",
    "locate": "Locate",
    "show_on_project": "Show on project",
    "saved_searches": "Saved searches",
    "no_filters": "No filters configured for this project yet.",
    "no_matches": "No units match the current filters.",
    "all_blocks": "All blocks",
    "no_phase_grouping": "No phase grouping",
    "select_floor": "Select a floor to see its units.",
    "filter": "Filter",
    "expand": "Expand",
    "collapse": "Collapse",
    "info": "Information",
    "read_more": "Read more",
    "navigate": "Navigate",
    "go_to_view": "Go to view",
    "details": "Details",
    "hide_info": "Hide info panel",
    "show_info": "Show info panel",
    "tour_360": "360° tour",
    "tour_360_available": "360° tour available",
    "zoom_in": "Zoom in",
    "zoom_out": "Zoom out",
    "motion_control": "Motion control",
    "fullscreen": "Fullscreen",
    "exit_fullscreen": "Exit fullscreen",
    "mute_music": "Mute music",
    "play_music": "Play music",
    "mute_background_music": "Mute background music",
    "play_background_music": "Play background music",
    "remove_from_shortlist": "Remove from shortlist",
    "showroom_refresh": "Refresh",
    "showroom_cache": "Cache for offline",
    "showroom_clear": "Clear offline files",
    "cleared": "Cleared",
    "done": "Done",
    "cancel": "Cancel",
    "retry": "Retry",
    "lead_failed": "Could not submit. Please try again.",
    "lead_invalid": "Enter your name and a valid phone number.",
    "lead_invalid_email": "Enter a valid email address.",
    "loading_action": "Please wait…",
    "unavailable_message": "This project is currently unavailable. Please contact the project owner for more information.",
    "final_installment": "Final instalment",
    "months": "months",
    "menu": "Menu",
    "back": "Back",
    "play": "Play",
    "scrub": "Drag to explore",
    "no_media": "No media available",
    "continue_portrait": "Continue in portrait",
    "select_language": "Language",
    "located": "Located",
    "dismiss": "Dismiss",
    "choose_location": "Choose a view",
    "building_view": "Building view",
    "floor_plan_view": "Floor plan view",
    "apartment": "Apartment",
    "villa": "Villa",
    "office": "Office",
    "shop": "Shop",
    "land": "Land",
    "studio": "Studio",
    "clear_filters": "Clear filters",
    "compare_limit": "Select up to four units to compare.",
    "select_units": "Select units",
    "all": "All"
  },
  "ar": {
    "close": "إغلاق",
    "find": "بحث",
    "find_unit": "البحث عن وحدة",
    "floors": "الطوابق",
    "location": "الموقع",
    "sort": "الترتيب",
    "recommended": "موصى به",
    "sort_price_asc": "السعر · من الأقل إلى الأعلى",
    "sort_price_desc": "السعر · من الأعلى إلى الأقل",
    "sort_area_asc": "المساحة · من الأقل إلى الأعلى",
    "sort_area_desc": "المساحة · من الأعلى إلى الأقل",
    "sort_floor_asc": "الطابق · من الأدنى إلى الأعلى",
    "sort_floor_desc": "الطابق · من الأعلى إلى الأدنى",
    "sort_code": "رمز الوحدة",
    "sort_status": "الحالة",
    "status": "الحالة",
    "floor": "الطابق",
    "unit": "الوحدة",
    "code": "رمز الوحدة",
    "type": "النوع",
    "view": "الإطلالة",
    "net_area": "المساحة الصافية",
    "gross_area": "المساحة الإجمالية",
    "price": "السعر",
    "currency": "العملة",
    "area": "المساحة",
    "area_sqm": "المساحة",
    "beds": "غرف النوم",
    "bedrooms": "غرف النوم",
    "bathrooms": "الحمامات",
    "phase": "المرحلة",
    "block": "المبنى",
    "phase_block": "المرحلة › المبنى",
    "any": "أي قيمة",
    "yes": "نعم",
    "no": "لا",
    "search": "بحث…",
    "min": "الأدنى",
    "max": "الأعلى",
    "from": "من",
    "to": "إلى",
    "locate": "تحديد الموقع",
    "show_on_project": "عرض على المشروع",
    "saved_searches": "عمليات البحث المحفوظة",
    "no_filters": "لم تُضبط فلاتر لهذا المشروع بعد.",
    "no_matches": "لا توجد وحدات تطابق الفلاتر الحالية.",
    "all_blocks": "جميع المباني",
    "no_phase_grouping": "دون تقسيم إلى مراحل",
    "select_floor": "اختر طابقًا لعرض وحداته.",
    "filter": "تصفية",
    "expand": "توسيع",
    "collapse": "طي",
    "info": "معلومات",
    "read_more": "قراءة المزيد",
    "navigate": "انتقال",
    "go_to_view": "الانتقال إلى المشهد",
    "details": "التفاصيل",
    "hide_info": "إخفاء لوحة المعلومات",
    "show_info": "إظهار لوحة المعلومات",
    "tour_360": "جولة 360°",
    "tour_360_available": "تتوفر جولة 360°",
    "zoom_in": "تكبير",
    "zoom_out": "تصغير",
    "motion_control": "التحكم بالحركة",
    "fullscreen": "ملء الشاشة",
    "exit_fullscreen": "الخروج من ملء الشاشة",
    "mute_music": "كتم الموسيقى",
    "play_music": "تشغيل الموسيقى",
    "mute_background_music": "كتم موسيقى الخلفية",
    "play_background_music": "تشغيل موسيقى الخلفية",
    "remove_from_shortlist": "إزالة من المفضلة",
    "showroom_refresh": "تحديث",
    "showroom_cache": "تنزيل للعمل دون اتصال",
    "showroom_clear": "مسح الملفات المحفوظة",
    "cleared": "تم المسح",
    "done": "تم",
    "cancel": "إلغاء",
    "retry": "إعادة المحاولة",
    "lead_failed": "تعذّر الإرسال. يرجى المحاولة مجددًا.",
    "lead_invalid": "أدخل اسمك ورقم هاتف صالحًا.",
    "lead_invalid_email": "أدخل عنوان بريد إلكتروني صالحًا.",
    "loading_action": "يرجى الانتظار…",
    "unavailable_message": "هذا المشروع غير متاح حاليًا. يرجى التواصل مع مالك المشروع لمزيد من المعلومات.",
    "final_installment": "القسط الأخير",
    "months": "أشهر",
    "menu": "القائمة",
    "back": "رجوع",
    "play": "تشغيل",
    "scrub": "اسحب للاستكشاف",
    "no_media": "لا توجد وسائط متاحة",
    "continue_portrait": "المتابعة بالوضع العمودي",
    "select_language": "اللغة",
    "located": "تم تحديد الموقع",
    "dismiss": "إخفاء",
    "choose_location": "اختر مشهدًا",
    "building_view": "منظر المبنى",
    "floor_plan_view": "مخطط الطابق",
    "apartment": "شقة",
    "villa": "فيلا",
    "office": "مكتب",
    "shop": "محل",
    "land": "أرض",
    "studio": "استوديو",
    "clear_filters": "مسح الفلاتر",
    "compare_limit": "اختر حتى أربع وحدات للمقارنة.",
    "select_units": "اختيار الوحدات",
    "all": "الكل"
  },
  "tr": {
    "close": "Kapat",
    "find": "Bul",
    "find_unit": "Daire bul",
    "floors": "Katlar",
    "location": "Konum",
    "sort": "Sırala",
    "recommended": "Önerilen",
    "sort_price_asc": "Fiyat · artan",
    "sort_price_desc": "Fiyat · azalan",
    "sort_area_asc": "Alan · artan",
    "sort_area_desc": "Alan · azalan",
    "sort_floor_asc": "Kat · artan",
    "sort_floor_desc": "Kat · azalan",
    "sort_code": "Daire kodu",
    "sort_status": "Durum",
    "status": "Durum",
    "floor": "Kat",
    "unit": "Daire",
    "code": "Daire kodu",
    "type": "Tip",
    "view": "Manzara",
    "net_area": "Net alan",
    "gross_area": "Brüt alan",
    "price": "Fiyat",
    "currency": "Para birimi",
    "area": "Alan",
    "area_sqm": "Alan",
    "beds": "Yatak odası",
    "bedrooms": "Yatak odası",
    "bathrooms": "Banyo",
    "phase": "Etap",
    "block": "Blok",
    "phase_block": "Etap › Blok",
    "any": "Tümü",
    "yes": "Evet",
    "no": "Hayır",
    "search": "Ara…",
    "min": "En az",
    "max": "En fazla",
    "from": "Başlangıç",
    "to": "Bitiş",
    "locate": "Konumunu göster",
    "show_on_project": "Projede göster",
    "saved_searches": "Kayıtlı aramalar",
    "no_filters": "Bu proje için henüz filtre ayarlanmadı.",
    "no_matches": "Geçerli filtrelere uygun daire yok.",
    "all_blocks": "Tüm bloklar",
    "no_phase_grouping": "Etap gruplaması yok",
    "select_floor": "Daireleri görmek için bir kat seçin.",
    "filter": "Filtre",
    "expand": "Genişlet",
    "collapse": "Daralt",
    "info": "Bilgi",
    "read_more": "Devamını oku",
    "navigate": "Gezin",
    "go_to_view": "Görünüme git",
    "details": "Ayrıntılar",
    "hide_info": "Bilgi panelini gizle",
    "show_info": "Bilgi panelini göster",
    "tour_360": "360° tur",
    "tour_360_available": "360° tur mevcut",
    "zoom_in": "Yakınlaştır",
    "zoom_out": "Uzaklaştır",
    "motion_control": "Hareketle kontrol",
    "fullscreen": "Tam ekran",
    "exit_fullscreen": "Tam ekrandan çık",
    "mute_music": "Müziği kapat",
    "play_music": "Müziği aç",
    "mute_background_music": "Arka plan müziğini kapat",
    "play_background_music": "Arka plan müziğini aç",
    "remove_from_shortlist": "Listeden kaldır",
    "showroom_refresh": "Yenile",
    "showroom_cache": "Çevrimdışı kullanım için indir",
    "showroom_clear": "Çevrimdışı dosyaları temizle",
    "cleared": "Temizlendi",
    "done": "Tamamlandı",
    "cancel": "İptal",
    "retry": "Tekrar dene",
    "lead_failed": "Gönderilemedi. Lütfen tekrar deneyin.",
    "lead_invalid": "Adınızı ve geçerli bir telefon numarası girin.",
    "lead_invalid_email": "Geçerli bir e-posta adresi girin.",
    "loading_action": "Lütfen bekleyin…",
    "unavailable_message": "Bu proje şu anda kullanılamıyor. Daha fazla bilgi için proje sahibiyle iletişime geçin.",
    "final_installment": "Son taksit",
    "months": "ay",
    "menu": "Menü",
    "back": "Geri",
    "play": "Oynat",
    "scrub": "Keşfetmek için sürükle",
    "no_media": "Medya mevcut değil",
    "continue_portrait": "Dikey devam et",
    "select_language": "Dil",
    "located": "Konum gösteriliyor",
    "dismiss": "Kapat",
    "choose_location": "Bir görünüm seçin",
    "building_view": "Bina görünümü",
    "floor_plan_view": "Kat planı görünümü",
    "apartment": "Daire",
    "villa": "Villa",
    "office": "Ofis",
    "shop": "Mağaza",
    "land": "Arsa",
    "studio": "Stüdyo",
    "clear_filters": "Filtreleri temizle",
    "compare_limit": "Karşılaştırmak için en fazla dört daire seçin.",
    "select_units": "Daire seç",
    "all": "Tümü"
  },
  "ru": {
    "close": "Закрыть",
    "find": "Поиск",
    "find_unit": "Найти объект",
    "floors": "Этажи",
    "location": "Расположение",
    "sort": "Сортировка",
    "recommended": "Рекомендуемые",
    "sort_price_asc": "Цена · по возрастанию",
    "sort_price_desc": "Цена · по убыванию",
    "sort_area_asc": "Площадь · по возрастанию",
    "sort_area_desc": "Площадь · по убыванию",
    "sort_floor_asc": "Этаж · по возрастанию",
    "sort_floor_desc": "Этаж · по убыванию",
    "sort_code": "Номер объекта",
    "sort_status": "Статус",
    "status": "Статус",
    "floor": "Этаж",
    "unit": "Объект",
    "code": "Номер объекта",
    "type": "Тип",
    "view": "Вид",
    "net_area": "Полезная площадь",
    "gross_area": "Общая площадь",
    "price": "Цена",
    "currency": "Валюта",
    "area": "Площадь",
    "area_sqm": "Площадь",
    "beds": "Спальни",
    "bedrooms": "Спальни",
    "bathrooms": "Ванные",
    "phase": "Этап",
    "block": "Блок",
    "phase_block": "Этап › Блок",
    "any": "Любой",
    "yes": "Да",
    "no": "Нет",
    "search": "Поиск…",
    "min": "Мин.",
    "max": "Макс.",
    "from": "От",
    "to": "До",
    "locate": "Найти на плане",
    "show_on_project": "Показать в проекте",
    "saved_searches": "Сохранённые поиски",
    "no_filters": "Фильтры для проекта ещё не настроены.",
    "no_matches": "Нет объектов, соответствующих фильтрам.",
    "all_blocks": "Все блоки",
    "no_phase_grouping": "Без группировки по этапам",
    "select_floor": "Выберите этаж, чтобы увидеть объекты.",
    "filter": "Фильтр",
    "expand": "Развернуть",
    "collapse": "Свернуть",
    "info": "Информация",
    "read_more": "Подробнее",
    "navigate": "Перейти",
    "go_to_view": "Перейти к виду",
    "details": "Подробнее",
    "hide_info": "Скрыть сведения",
    "show_info": "Показать сведения",
    "tour_360": "Тур 360°",
    "tour_360_available": "Доступен тур 360°",
    "zoom_in": "Приблизить",
    "zoom_out": "Отдалить",
    "motion_control": "Управление движением",
    "fullscreen": "На весь экран",
    "exit_fullscreen": "Выйти из полноэкранного режима",
    "mute_music": "Выключить музыку",
    "play_music": "Включить музыку",
    "mute_background_music": "Выключить фоновую музыку",
    "play_background_music": "Включить фоновую музыку",
    "remove_from_shortlist": "Убрать из подборки",
    "showroom_refresh": "Обновить",
    "showroom_cache": "Скачать для работы офлайн",
    "showroom_clear": "Удалить офлайн-файлы",
    "cleared": "Очищено",
    "done": "Готово",
    "cancel": "Отмена",
    "retry": "Повторить",
    "lead_failed": "Не удалось отправить. Попробуйте ещё раз.",
    "lead_invalid": "Укажите имя и корректный номер телефона.",
    "lead_invalid_email": "Укажите корректный адрес электронной почты.",
    "loading_action": "Подождите…",
    "unavailable_message": "Проект сейчас недоступен. За подробностями обратитесь к владельцу проекта.",
    "final_installment": "Последний платёж",
    "months": "месяцев",
    "menu": "Меню",
    "back": "Назад",
    "play": "Воспроизвести",
    "scrub": "Перетащите для просмотра",
    "no_media": "Нет доступных материалов",
    "continue_portrait": "Продолжить вертикально",
    "select_language": "Язык",
    "located": "Найдено",
    "dismiss": "Скрыть",
    "choose_location": "Выберите вид",
    "building_view": "Вид здания",
    "floor_plan_view": "План этажа",
    "apartment": "Квартира",
    "villa": "Вилла",
    "office": "Офис",
    "shop": "Магазин",
    "land": "Участок",
    "studio": "Студия",
    "clear_filters": "Сбросить фильтры",
    "compare_limit": "Выберите до четырёх объектов для сравнения.",
    "select_units": "Выбрать объекты",
    "all": "Все"
  }
};
for (const lang of Object.keys(EXTRA_LABELS)) Object.assign(VIEWER_LABELS[lang], EXTRA_LABELS[lang]);

VIEWER_LABELS.en.units_count = (n) => `${n} ${n === 1 ? "unit" : "units"}`;
VIEWER_LABELS.ru.units_count = (n) => `${n} ${{ one: "объект", few: "объекта", many: "объектов", other: "объекта" }[new Intl.PluralRules("ru").select(n)]}`;
VIEWER_LABELS.en.matches_count = (n) => `${n} ${n === 1 ? "match" : "matches"}`;
VIEWER_LABELS.ar.matches_count = (n) => `النتائج: ${n}`;
VIEWER_LABELS.tr.matches_count = (n) => `${n} sonuç`;
VIEWER_LABELS.ru.matches_count = (n) => `${n} ${{ one: "результат", few: "результата", many: "результатов", other: "результата" }[new Intl.PluralRules("ru").select(n)]}`;

const SHOWROOM_LABELS = {
  "en": {
    "showroom_description": "Download this project for offline viewing. Refresh after project updates.",
    "showroom_partial": "Download incomplete — retry before going offline.",
    "showroom_retry": "Retry download",
    "showroom_refresh": "Refresh download",
    "showroom_download": "Download for offline viewing",
    "showroom_cleared": "Offline files cleared",
    "showroom_done": "Download complete",
    "showroom_items": "files",
    "showroom_cached": "downloaded",
    "showroom_failed_items": "failed",
    "showroom_needs_refresh": "Refresh the download to verify offline availability."
  },
  "ar": {
    "showroom_description": "نزّل هذا المشروع للعرض دون اتصال. حدّث التنزيل بعد تحديث المشروع.",
    "showroom_partial": "التنزيل غير مكتمل — أعد المحاولة قبل قطع الاتصال.",
    "showroom_retry": "إعادة التنزيل",
    "showroom_refresh": "تحديث التنزيل",
    "showroom_download": "تنزيل للعرض دون اتصال",
    "showroom_cleared": "تم مسح الملفات المحفوظة",
    "showroom_done": "اكتمل التنزيل",
    "showroom_items": "ملفات",
    "showroom_cached": "تم تنزيلها",
    "showroom_failed_items": "تعذّر تنزيلها",
    "showroom_needs_refresh": "حدّث التنزيل للتحقق من توفر المشروع دون اتصال."
  },
  "tr": {
    "showroom_description": "Bu projeyi çevrimdışı görüntülemek için indirin. Proje güncellendiğinde indirmeyi yenileyin.",
    "showroom_partial": "İndirme tamamlanmadı — çevrimdışı olmadan önce tekrar deneyin.",
    "showroom_retry": "İndirmeyi tekrar dene",
    "showroom_refresh": "İndirmeyi yenile",
    "showroom_download": "Çevrimdışı görüntülemek için indir",
    "showroom_cleared": "Çevrimdışı dosyalar temizlendi",
    "showroom_done": "İndirme tamamlandı",
    "showroom_items": "dosya",
    "showroom_cached": "indirildi",
    "showroom_failed_items": "başarısız",
    "showroom_needs_refresh": "Çevrimdışı kullanılabilirliği doğrulamak için indirmeyi yenileyin."
  },
  "ru": {
    "showroom_description": "Скачайте проект для просмотра офлайн. Обновляйте загрузку после изменений проекта.",
    "showroom_partial": "Загрузка неполная — повторите попытку перед переходом в офлайн.",
    "showroom_retry": "Повторить загрузку",
    "showroom_refresh": "Обновить загрузку",
    "showroom_download": "Скачать для просмотра офлайн",
    "showroom_cleared": "Офлайн-файлы удалены",
    "showroom_done": "Загрузка завершена",
    "showroom_items": "файлов",
    "showroom_cached": "загружено",
    "showroom_failed_items": "ошибок",
    "showroom_needs_refresh": "Обновите загрузку, чтобы проверить доступность офлайн."
  }
};
for (const lang of Object.keys(SHOWROOM_LABELS)) Object.assign(VIEWER_LABELS[lang], SHOWROOM_LABELS[lang]);

const NAV_LABELS = {
  "en": {
    "no_floor_blocks": "No blocks with floor information.",
    "no_floor_units": "No units with floor information.",
    "vr_mode": "VR mode",
    "media_unavailable": "Media unavailable",
    "matches": "matches",
    "reset": "Reset",
    "pick_journey": "Choose a presentation",
    "steps": "steps",
    "show_unit_in": "Show unit in",
    "tap_to_begin": "Tap to begin"
  },
  "ar": {
    "no_floor_blocks": "لا توجد مبانٍ تحتوي معلومات طوابق.",
    "no_floor_units": "لا توجد وحدات تحتوي معلومات طوابق.",
    "vr_mode": "وضع الواقع الافتراضي",
    "media_unavailable": "الوسائط غير متاحة",
    "matches": "نتائج",
    "reset": "إعادة الضبط",
    "pick_journey": "اختر عرضًا تقديميًا",
    "steps": "خطوات",
    "show_unit_in": "عرض الوحدة في",
    "tap_to_begin": "اضغط للبدء"
  },
  "tr": {
    "no_floor_blocks": "Kat bilgisi olan blok yok.",
    "no_floor_units": "Kat bilgisi olan daire yok.",
    "vr_mode": "VR modu",
    "media_unavailable": "Medya kullanılamıyor",
    "matches": "sonuç",
    "reset": "Sıfırla",
    "pick_journey": "Bir sunum seçin",
    "steps": "adım",
    "show_unit_in": "Daireyi göster",
    "tap_to_begin": "Başlamak için dokunun"
  },
  "ru": {
    "no_floor_blocks": "Нет блоков с информацией об этажах.",
    "no_floor_units": "Нет объектов с информацией об этажах.",
    "vr_mode": "Режим VR",
    "media_unavailable": "Материалы недоступны",
    "matches": "результатов",
    "reset": "Сбросить",
    "pick_journey": "Выберите презентацию",
    "steps": "шагов",
    "show_unit_in": "Показать объект",
    "tap_to_begin": "Нажмите, чтобы начать"
  }
};
for (const lang of Object.keys(NAV_LABELS)) Object.assign(VIEWER_LABELS[lang], NAV_LABELS[lang]);

VIEWER_LABELS.en.project_not_found = "Project not found or unavailable.";
VIEWER_LABELS.ar.project_not_found = "المشروع غير موجود أو غير متاح.";
VIEWER_LABELS.tr.project_not_found = "Proje bulunamadı veya kullanılamıyor.";
VIEWER_LABELS.ru.project_not_found = "Проект не найден или недоступен.";
VIEWER_LABELS.en.facade_view = "Facade view";
VIEWER_LABELS.ar.facade_view = "منظر الواجهة";
VIEWER_LABELS.tr.facade_view = "Cephe görünümü";
VIEWER_LABELS.ru.facade_view = "Вид фасада";

export function L(lang, key, ...args) {
  const dict = VIEWER_LABELS[lang] || VIEWER_LABELS.en;
  const v = dict[key] ?? VIEWER_LABELS.en[key];
  return typeof v === "function" ? v(...args) : v;
}

export const RTL_LANGS = new Set(["ar", "he", "fa", "ur"]);
export const LANG_NAMES = { en: "EN", ar: "ع", tr: "TR", ru: "RU" };
